import {
  Button,
  Checkbox,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormContainerProps } from "react-hook-form-mui";
import LegalbirdIoModal from "../Modal/LegalbirdIoModal";
import SubmitButton from "../Button/SubmitButton";
import ContentBox from "../ContentBox/ContentBox";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { fetchCollection, queryKeys } from "../../services/ReactQuery/reactQueryService";
import { requiredValidator } from "../../services/reactHookFormValidationRules";
import { getDisplayedType } from "../../services/bookingEntryService";
import { BookingEntry } from "../../types/BookingEntry";
import { MediaObject } from "../../types/MediaObject";
import {
  getMediaObjectFilters,
  getMediaObjectFormattedDescription,
  getRenderValue,
} from "../../services/mediaObjectService";
import SelfIssuedReceiptBookingData from "./SelfIssuedReceiptBookingData";
import NumberFieldElement from "../ReactHookFormElements/NumberFieldElement";
import { apiPost } from "../../services/Api/apiCall";
import { convertBooleanToYesNoString } from "../../services/formServiceFunctions";
import { ProductUnion } from "../../types/ProductUnion";
import MultiSelectElement from "../ReactHookFormElements/MultiSelectElement";
import TextFieldElement from "../ReactHookFormElements/TextFieldElement";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs, { Dayjs } from "dayjs";

const formSchema = z.object({
  paymentOrRefundReason: z.string().and(requiredValidator()),
  paymentOutgoingAmount: z.string().nullable().and(requiredValidator()),
  paymentOutgoingRecipient: z.string().and(requiredValidator()),
  bookingData: z.array(
    z.object({
      id: z.number().optional(),
      isThirdPartyFunds: z.string().and(requiredValidator()),
      paymentReceivedFrom: z.string().optional(),
      bookingDate: z.custom<Dayjs>().optional(),
      amount: z.string().optional(),
    })
  ),
  otherPaymentReason: z.string().optional(),
  description: z.string().optional(),
  selectedMediaObjects: z.array(z.number()),
});

type SelfIssuedReceiptProps = {
  product: ProductUnion;
  modalOpen: boolean;
  setModalOpen: Function;
  selectedBookingEntryIds: number[];
  setSelectedBookingEntryIds: Function;
};

const SelfIssuedReceipt = ({
  product,
  modalOpen,
  setModalOpen,
  selectedBookingEntryIds = [],
  setSelectedBookingEntryIds,
}: SelfIssuedReceiptProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();

  const mediaObjectsFilter = getMediaObjectFilters(product, currentUser);
  let bookingFilter = {
    productId: product?.id,
    deleted: false,
    type: "payment",
    debitOrCredit: "credit",
  };

  const { data: bookingEntriesCollection } = useQuery({
    queryKey: queryKeys.collection("booking_entries", bookingFilter),

    queryFn: () =>
      fetchCollection("booking_entries", bookingFilter)
  });

  const { data: mediaObjectsCollection } = useQuery({
    queryKey: queryKeys.collection("media_objects", mediaObjectsFilter),

    queryFn: () =>
      fetchCollection("media_objects", mediaObjectsFilter)
  });

  const formContext = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      paymentOrRefundReason: "",
      otherPaymentReason: "",
      description: "",
      bookingData: [],
      paymentOutgoingAmount: null,
      paymentOutgoingRecipient: "",
      selectedMediaObjects: [],
    },
    resolver: zodResolver(formSchema),
  });

  const { handleSubmit } = formContext;
  function getChangedBookingEntryId(oldIds: number[], newIds: number[]): number | undefined {
    return oldIds.find((id) => !newIds.includes(id)) ?? newIds.find((id) => !oldIds.includes(id));
  }

  useEffect(() => {
    handleBookingEntryChange([], selectedBookingEntryIds);
  }, []);

  const handleBookingEntryChange = (selectedBookingEntryIds: number[], updatedSelectedBookingEntryIds: number[]) => {
    setSelectedBookingEntryIds(updatedSelectedBookingEntryIds);

    const changedBookingEntryId = getChangedBookingEntryId(selectedBookingEntryIds, updatedSelectedBookingEntryIds);
    if (!changedBookingEntryId) {
      return;
    }

    const bookingEntry = bookingEntries.find((bookingEntry: BookingEntry) => bookingEntry.id === changedBookingEntryId);
    if (!bookingEntry) {
      return;
    }

    //checks if updatedSelectedBookingEntryIds was added or removed
    const index = updatedSelectedBookingEntryIds.findIndex((id: number) => id === changedBookingEntryId);

    // updatedSelectedBookingEntryIds was removed
    if (index === -1) {
      const bookingData = formContext.getValues("bookingData") || [];
      const updatedBookingData = bookingData.filter(
        (item: NonNullable<z.infer<typeof formSchema>["bookingData"]>[number]) => item.id !== changedBookingEntryId
      );
      formContext.setValue("bookingData", updatedBookingData);

      return;
    }
    // updatedSelectedBookingEntryIds was added
    formContext.setValue(`bookingData.${index}.id`, bookingEntry.id);
    formContext.setValue(`bookingData.${index}.bookingDate`, dayjs(bookingEntry.bookingDate));
    formContext.setValue(`bookingData.${index}.amount`, bookingEntry.amount?.toString() ?? "");
    formContext.setValue(
      `bookingData.${index}.isThirdPartyFunds`,
      bookingEntry.isThirdPartyFunds !== null ? convertBooleanToYesNoString(bookingEntry.isThirdPartyFunds) : null
    );
  };

  const onSubmit: FormContainerProps["onSuccess"] = async (values) => {
    setIsLoading(true);
    let message = "Eigenbeleg wurde generiert";
    let snackbarVariant: "custom" | "error" = "custom";

    try {
      await apiPost(`/lb/services/generate_case_file`, {
        createdBy: currentUser["@id"],
        productClassName: product.productClassName,
        productId: product.id,
        documentIdentifier: "selfIssuedReceipt",
        params: {
          ...values,
          createdBy: currentUser.person.fullname,
        },
      });
    } catch (e) {
      message = "Eigenbeleg konnte nicht erstellt werden";
      snackbarVariant = "error";
    }

    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("media_objects", mediaObjectsFilter)
    });

    enqueueSnackbar(message, {
      variant: snackbarVariant,
      isNonInteractive: true,
      anchorOrigin: { vertical: "bottom", horizontal: "center" },
    });

    setSelectedBookingEntryIds();
    setModalOpen(false);
    setIsLoading(false);
  };

  if (!product || !mediaObjectsCollection || !bookingEntriesCollection) {
    return null;
  }

  const mediaObjects = mediaObjectsCollection["hydra:member"];
  const bookingEntries = bookingEntriesCollection["hydra:member"];

  const bookingEntriesOptions = bookingEntries.map((bookingEntry: BookingEntry) => ({
    id: bookingEntry.id,
    label: `${getDisplayedType(bookingEntry)} | ${dayjs(bookingEntry.created).format("DD.MM.YYYY HH:mm")}h | ${
      bookingEntry.amount
    } €`,
  }));

  return (
    <>
      <ContentBox headline="Eigenbeleg erstellen">
        <Typography sx={{ textAlign: "center", marginY: "2px" }}>
          Erstellung eines Eigenbelegs bzw. einer Aktennotiz. Das daraus entstehende Dokument wird automatisch im Fall
          gespeichert.
        </Typography>
        <Stack
          spacing={3}
          sx={{
            alignItems: "center",
            mt: "4rem",
          }}
        >
          <Button variant="contained" onClick={() => setModalOpen(true)} sx={{ width: "350px" }}>
            Eigenbeleg erstellen
          </Button>
        </Stack>
      </ContentBox>
      <LegalbirdIoModal
        title="Neuen Eigenbeleg erstellen"
        open={modalOpen}
        handleClose={() => {
          setModalOpen(false);
          setSelectedBookingEntryIds();
          formContext.reset();
        }}
        submitButton={
          <SubmitButton variant="contained" isLoading={isLoading} onClick={handleSubmit(onSubmit)}>
            Eigenbeleg erstellen
          </SubmitButton>
        }
      >
        <FormProvider {...formContext}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              <Grid size={12}>
                <Typography sx={{ marginBottom: "16px" }}>Wofür ist der Eigenbeleg?</Typography>
                <MultiSelectElement
                  name={"paymentOrRefundReason"}
                  label={"Eigenbeleg zu"}
                  options={[
                    { id: "paymentOfCourtCostSurplus", label: "Auszahlung Gerichtskostenüberschuss (Fremdgeld)" },
                    {
                      id: "paymentOfFeeToLegalExpensesInsurance",
                      label: "Rückzahlung Gebühren an Rechtsschutzversicherung",
                    },
                    { id: "paymentOfClientClaim", label: "Auszahlung einer Mandantenforderung (Fremdgeld)" },
                    {
                      id: "paymentOrRefundOfThirdParties",
                      label: "Rückzahlung / Auszahlung an Dritte (z.B. Gegenseite)",
                    },
                    { id: "otherPayment", label: "Sonstiges" },
                  ]}
                />
              </Grid>
              {formContext.watch("paymentOrRefundReason") === "otherPayment" && (
                <Grid size={12}>
                  <Typography>Sonstiges, nämlich:</Typography>
                  <TextFieldElement
                    name="otherPaymentReason"
                    label="Sonstiges, nämlich:"
                    fullWidth
                    multiline
                    rows={3}
                    slotProps={{
                      htmlInput: {
                        maxLength: 255,
                      },
                    }}
                  />
                </Grid>
              )}

              <Grid size={12}>
                <Typography>Beschreibung</Typography>
                <TextFieldElement name={"description"} label={"Beschreibung"} fullWidth multiline rows={3} />
              </Grid>

              <Grid sx={{ padding: "1rem 0" }} size={12}>
                <Typography sx={{ marginBottom: "16px" }}>Welche Buchungen sind betroffen?</Typography>
                <FormControl fullWidth>
                  <InputLabel id="select-booking-entry-label">Buchung</InputLabel>
                  <Select
                    name={"selectedBookingEntryIds"}
                    labelId="select-booking-entry-label"
                    label={"Buchung"}
                    multiple
                    fullWidth
                    onChange={(event) => {
                      handleBookingEntryChange(selectedBookingEntryIds, event.target.value as number[]);
                    }}
                    value={selectedBookingEntryIds}
                  >
                    {bookingEntriesOptions.map((bookingEntriesOption: { id: BookingEntry["id"]; label: string }) => (
                      <MenuItem key={bookingEntriesOption.id} value={bookingEntriesOption.id}>
                        <Checkbox checked={selectedBookingEntryIds.includes(bookingEntriesOption.id)} />
                        {bookingEntriesOption.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {selectedBookingEntryIds.map((bookingEntryId: BookingEntry["id"], i: number) => {
                return (
                  <React.Fragment key={bookingEntryId}>
                    {i > 0 && <Divider sx={{ width: "100%", margin: "1.4rem 0 0.6rem" }} />}
                    <SelfIssuedReceiptBookingData index={i} />
                  </React.Fragment>
                );
              })}

              <Divider sx={{ width: "100%" }} />

              <Grid size={12}>
                <Typography>An wen wurde die Auszahlung geleistet?</Typography>
                <TextFieldElement
                  name={"paymentOutgoingRecipient"}
                  label="Überweisungsempfänger"
                  fullWidth
                  slotProps={{
                    htmlInput: {
                      maxLength: 255,
                    },
                  }}
                />
              </Grid>

              <Grid size={12}>
                <Typography>Welchen Betrag hatte die Auszahlung?</Typography>
                <NumberFieldElement
                  name={"paymentOutgoingAmount"}
                  label="Höhe der Auszahlung"
                  fullWidth
                  fieldType="money"
                  numberType={"float"}
                />
              </Grid>

              {mediaObjects && (
                <Grid sx={{ padding: "1rem 0" }} size={12}>
                  <Typography>Datei anhängen</Typography>
                  <MultiSelectElement
                    multiple
                    fullWidth
                    label="Datei anhängen"
                    name="selectedMediaObjects"
                    options={mediaObjects
                      .filter((mediaObject: MediaObject) => mediaObject.mimeType === "application/pdf")
                      .map((mediaObject: MediaObject) => ({
                        id: mediaObject.id,
                        label: getMediaObjectFormattedDescription(mediaObject),
                      }))}
                    showCheckbox={true}
                    renderValue={(mediaObjectIds: any) => getRenderValue(mediaObjectIds, mediaObjects)}
                  />
                </Grid>
              )}
            </Grid>
          </form>
        </FormProvider>
      </LegalbirdIoModal>
    </>
  );
};
export default SelfIssuedReceipt;
